import { Text, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { LegalTypeFormSelect } from "features/entities/LegalTypeFormSelect";
import AddPortfolioModal from "features/investments/AddPortfolioModal";
import { AllocationMethodologyTitle } from "features/investments/AllocationMethodologyTitle";
import {
  useInvestmentCreate,
  useInvestmentList,
  useInvestmentRetrieveSuspense,
  useInvestmentUpdate,
} from "gen/endpoints/investment/investment";
import { InvestmentEntityCreateEditAPIDataRequest } from "gen/model/investmentEntityCreateEditAPIDataRequest";
import { InvestmentEntityData } from "gen/model/investmentEntityData";
import { InvestmentEntityReadAPIData } from "gen/model/investmentEntityReadAPIData";
import { InvestmentLocationCountryEnum } from "gen/model/investmentLocationCountryEnum";
import { InvestmentOrPortfolioEnum } from "gen/model/investmentOrPortfolioEnum";
import { InvestmentTypeEnum } from "gen/model/investmentTypeEnum";
import { RealEstateInvestmentTypeEnum } from "gen/model/realEstateInvestmentTypeEnum";
import {
  investmentCreateBody,
  investmentUpdateBody,
} from "gen/schemas/investment/investment";
import { noop } from "lodash-es";
import { useCallback, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BooleanToggleButtonGroup,
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import { FormProps } from "shared/components/form/Form";
import { useFormDrawerInitialFocusRef } from "shared/components/form/FormDrawer";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { enumKeys } from "shared/utils/EnumUtils";

export const InvestmentTypeFormSelect: React.FC = () => {
  const { t } = useTranslation(["investment", "enums"]);
  return (
    <FormSelect
      name="metadata.investment_type"
      title={t("investment:investmentType")}
    >
      {enumKeys(InvestmentTypeEnum).map((investment_type_enum) => (
        <SelectOption
          key={`investment_type_${investment_type_enum}`}
          value={investment_type_enum}
        >
          {t(`enums:InvestmentTypeEnum.${investment_type_enum}`)}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

export const RealEstateInvestmentTypeFormSelect: React.FC = () => {
  const { t } = useTranslation(["investment", "enums"]);
  return (
    <FormSelect
      name="metadata.real_estate_investment_type"
      title={t("investment:realEstateInvestmentType")}
      isOptional={true}
      unsetWhenEmpty={true}
    >
      {enumKeys(RealEstateInvestmentTypeEnum).map(
        (real_estate_investment_type_enum) => (
          <SelectOption
            key={real_estate_investment_type_enum}
            value={real_estate_investment_type_enum}
          >
            {t(
              `enums:RealEstateInvestmentTypeEnum.${real_estate_investment_type_enum}`,
            )}
          </SelectOption>
        ),
      )}
    </FormSelect>
  );
};

export const InvestmentLocationFormToggle: React.FC = () => {
  const { t } = useTranslation(["investment"]);
  return (
    <FormToggleButtonGroup
      name="metadata.investment_location.country"
      title={t("investment:investmentLocation")}
    >
      <ToggleButton value={InvestmentLocationCountryEnum.usa}>
        {t("investment:investmentLocations.usa")}
      </ToggleButton>
      <ToggleButton value={InvestmentLocationCountryEnum.foreign}>
        {t("investment:investmentLocations.foreign")}
      </ToggleButton>
    </FormToggleButtonGroup>
  );
};

const PortfolioSelectOrCreate: React.FC<{
  portfolios?: InvestmentEntityData[];
  onCreate: (name: string) => void;
}> = ({ portfolios, onCreate }) => {
  const { t } = useTranslation(["investment"]);
  return (
    <FormSelect
      unsetWhenEmpty={true}
      name="portfolio_id"
      title={t("investment:portfolioOptional")}
      createObjectName={t("investment:portfolio")}
      isCreatable={true}
      onCreateOption={onCreate}
    >
      {portfolios?.map((portfolio) => (
        <SelectOption key={portfolio.id} value={portfolio.id!}>
          {portfolio.legal_name}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

interface InvestmentEditFormProps {
  fundFamilyId: string;
  investmentId: string;
  investment?: InvestmentEntityCreateEditAPIDataRequest;
}

interface InvestmentAddFormProps {
  fundFamilyId: string;
}

export const useInvestmentAddForm = ({
  fundFamilyId,
}: InvestmentAddFormProps): FormProps<
  InvestmentEntityData,
  InvestmentEntityData
> => {
  const { t } = useTranslation(["investment"]);

  const { mutateAsync: createInvestment } = useInvestmentCreate();

  const onSubmit = useCallback(
    async (data: InvestmentEntityData) =>
      await createInvestment({ fundFamilyId, data }),
    [createInvestment, fundFamilyId],
  );

  const form = useForm<InvestmentEntityData>({
    resolver: zodResolver(investmentCreateBody),
    defaultValues: {
      metadata: {
        investment_or_portfolio: "investment",
        is_allocable: true,
      },
    },
  });

  return {
    form,
    onSubmit,
    successMessage: t("investment:add.success"),
  } as const;
};

export const useInvestmentEditForm = ({
  fundFamilyId,
  investmentId,
}: InvestmentEditFormProps): FormProps<
  InvestmentEntityData,
  InvestmentEntityData
> => {
  const { t } = useTranslation(["investment"]);

  const { data: investmentData } = useInvestmentRetrieveSuspense(
    fundFamilyId,
    investmentId,
  );

  const { mutateAsync: updateInvestment } = useInvestmentUpdate();

  const onSubmit = async (
    data: InvestmentEntityCreateEditAPIDataRequest,
  ): Promise<InvestmentEntityReadAPIData> =>
    await updateInvestment({
      fundFamilyId,
      id: investmentId,
      data,
    });

  const form = useForm<InvestmentEntityCreateEditAPIDataRequest>({
    resolver: zodResolver(investmentUpdateBody),
    defaultValues: investmentData,
  });

  return {
    form,
    onSubmit,
    successMessage: t("investment:edit.success"),
  } as const;
};

export const InvestmentFormFields: React.FC<{
  fundFamilyId: string;
}> = ({ fundFamilyId }) => {
  const { t } = useTranslation(["common", "investment", "entity"]);

  const { data: portfolios, refetch: refetchPortfolios } = useInvestmentList(
    fundFamilyId,
    {
      investment_or_portfolio: "portfolio",
    },
  );

  const form = useFormContext();
  const investmentOrPortfolio = form.watch("metadata.investment_or_portfolio");

  const initialFocusRef = useFormDrawerInitialFocusRef();

  const portfolioId = form.watch("portfolio_id");
  const investmentType = form.watch("metadata.investment_type");

  const portfolio = portfolios?.find(
    (portfolio) => portfolio.id === portfolioId,
  );

  const addPortfolioOnSuccess = async (portfolio: InvestmentEntityData) => {
    await refetchPortfolios();
    form.setValue("portfolio_id", portfolio.id);
  };

  const {
    isOpen: isCreatePortfolioDialogOpen,
    onOpen: openCreatePortfolioDialog,
    onClose: closeCreatePortfolioDialog,
  } = useDisclosure();

  const [addPortfolioInitialName, setAddPortfolioInitialName] =
    useState<string>();

  return (
    <>
      <FormTextInput
        name="legal_name"
        title={t("investment:investmentName.full")}
        ref={initialFocusRef}
      />
      <FormTextInput
        name="short_name"
        title={t("common:optional", {
          field_name: t("investment:investmentName.short"),
        })}
      />
      <FormTextInput name="external_id" title={t("entity:externalId")} />
      <InvestmentTypeFormSelect />
      <LegalTypeFormSelect />
      {investmentOrPortfolio === InvestmentOrPortfolioEnum.investment && (
        <>
          <PortfolioSelectOrCreate
            portfolios={portfolios}
            onCreate={(name) => {
              setAddPortfolioInitialName(name);
              openCreatePortfolioDialog();
            }}
          />
          <Text>{portfolio?.metadata.description}</Text>
          <BooleanToggleButtonGroup
            title={<AllocationMethodologyTitle />}
            trueText={t("investment:portfolio")}
            falseText={t("investment:investment")}
            onChange={noop}
            value={portfolio?.metadata.is_allocable}
          />
        </>
      )}
      {isCreatePortfolioDialogOpen && (
        <AddPortfolioModal
          investmentType={investmentType}
          initialName={addPortfolioInitialName}
          onSuccess={addPortfolioOnSuccess}
          onCloseComplete={() => {
            closeCreatePortfolioDialog();
            setAddPortfolioInitialName(undefined);
          }}
        />
      )}
    </>
  );
};

export const InvestmentFormFieldsOptional = () => {
  const { t } = useTranslation(["common", "investment"]);
  return (
    <>
      <FormDateInput
        name="metadata.acquisition_date"
        title={t("investment:acquisitionDate")}
      />
      <FormDateInput
        name="metadata.disposition_date"
        title={t("investment:dispositionDate")}
      />
      <InvestmentLocationFormToggle />
      <FormMoneyInput
        name="metadata.target_amount"
        title={t("investment:targetAmount")}
      />
      <RealEstateInvestmentTypeFormSelect />
      <FormTextArea
        name="metadata.description"
        title={t("investment:description")}
      />
    </>
  );
};
